import {
  BusinessTypesOptions,
  getFormationYearOptions,
  FillingTypeOptions,
  TaxIdentificationOptions,
} from 'app/features/profile/businessDetails/addBusinessDetails/formFields';
import { IFincenReport, IFincenReportForm } from 'app/services/fincenReports';
import { Fragment, useEffect } from 'react';
import renderInput from 'utils/renderInput';
import { Validators } from 'utils/Validators';
import * as Yup from 'yup';
// Automatically set the tax year to the next year
const nextYear = new Date().getFullYear() + 1;
const FormationYearOptions = getFormationYearOptions(); // Ensure this always returns an array
export const validationSchema = Yup.object().shape({
  // General Info
  year: Yup.number().nullable().required('Required'), // We'll still require it for backend
  type: Yup.string().nullable().required('Required'),
  business_id: Yup.string().when('type', {
    is: 'business',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  // Business Type
  businessType: Yup.string().when('type', {
    is: 'business',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  submittedbyfirstname: Yup.string()
    .nullable()
    .required('First Name is required'),
  submittedbylastname: Yup.string()
    .nullable()
    .required('Last Name is required'),
  ssn: Yup.string()
    .matches(/^\d{3}-\d{2}-\d{4}$/, 'Invalid SSN format')
    .nullable()
    .required('Social Security Number is required'),
  phoneNumber: Yup.string()
    .matches(/^\d+$/, 'Invalid phone number')
    .nullable()
    .required('Phone Number is required'),
  submittedbyemail: Yup.string()
    .email('Invalid email')
    .nullable()
    .required('Email is required'),

  // Company Details & Formation Year
  companyname: Yup.string().nullable().required('Company Name is required'),
  companyEmail: Yup.string()
    .email('Invalid email')
    .nullable()
    .required('Company Email Address is required'),
  foreignpoolediv: Yup.boolean(),
  getfincenid: Yup.boolean(),
  formationyear: Yup.string().nullable().required('Formation Year is required'),

  // Filing Type
  reporttype: Yup.string().nullable().required('Filing Type is required'),

  // Tax Information
  taxtype: Yup.string()
    .nullable()
    .required('Tax Identification Type is required'),
  taxnumber: Yup.string().when('taxtype', {
    is: 'EIN',
    then: Yup.string()
      .matches(/^\d{2}-\d{7}$/, 'Invalid EIN format')
      .nullable()
      .required('Tax Identification Number (EIN) is required'),
    otherwise: Yup.string()
      .matches(/^\d{9}$/, 'Invalid Tax Identification Number format')
      .nullable()
      .required('Tax Identification Number is required'),
  }),
  taxcountry: Yup.string().when('taxtype', {
    is: 'Foreign',
    then: Yup.string().nullable().required('Country/Jurisdiction is required'),
    otherwise: Yup.string().nullable(),
  }),
  countryjurisdiction: Yup.string()
    .nullable()
    .required('Country/Jurisdiction of Formation is required'),
  formationstate: Yup.string().when('countryjurisdiction', {
    is: 'USA',
    then: Yup.string().nullable().required('State of Formation is required'),
    otherwise: Yup.string().nullable(),
  }),
  tribaljurisdiction: Yup.string().nullable(),

  // Address
  mailingaddress: Yup.string().nullable().required('Address is required'),
  state: Yup.string().nullable().required('State is required'),
  mailingcity: Yup.string().nullable().required('City is required'),
  zip: Yup.string()
    .matches(/^\d{5}$/, 'Invalid Zip Code format')
    .nullable()
    .required('Zip Code is required'),

  // FinCEN Report Information
  notes: Yup.string(),
  reachPhoneNumber: Yup.string()
    .matches(/^\d+$/, 'Invalid phone number')
    .nullable(),
  whatIsTheBestTimeToReachYouAtThisNumber: Yup.string()
    .oneOf(['Anytime', 'Morning', 'Afternoon', 'Evening'])
    .nullable()
    .required('Best time to reach is required'),
  agreement: Yup.boolean().nullable().oneOf([true], 'Agreement is required'),
});

export const FincenReportFields = [
  {
    type: 'information',
    subLabel: () => (
      <div className="text-sm text-gray-700">
        <span className="text-green font-semibold mr-2">Instructions</span>
        Please provide the required details to submit a FinCEN Beneficial
        Ownership Information (BOI) report. Ensure all company information,
        including formation year, tax ID, and business address, is accurate.
        This information will be used to file a formal report disclosing
        beneficial ownership as required by FinCEN regulations. Accurate and
        complete submission helps maintain compliance and transparency for your
        organization.
      </div>
    ),
  },

  {
    dependenciesTitle: 'General Info',
    type: 'information',
    dependencies: [
      {
        label: 'First Name *',
        name: 'submittedbyfirstname',
        type: 'text',
      },
      {
        label: 'Last Name *',
        name: 'submittedbylastname',
        type: 'text',
      },
      {
        label: 'Social Security Number *',
        name: 'ssn',
        type: 'maskedInput',
        mask: '999-99-9999',
      },
      {
        label: 'Phone Number *',
        name: 'phoneNumber',
        type: 'text',
      },
      {
        label: 'Email *',
        name: 'submittedbyemail',
        type: 'text',
      },
      {
        name: 'type',
        type: 'radio',
        label: 'Tax Type *',
        options: [
          { label: 'Individual', value: 'individual' },
          { label: 'Business', value: 'business' },
        ],
        dependencies: [
          {
            predicate: (v) => v === 'business',
            type: 'business',
            label: 'Select Business',
            name: 'business_id',
          },
          {
            predicate: (v) => v === 'business',
            type: 'select',
            name: 'businessType',
            label: 'Business Type',
            options: BusinessTypesOptions,
          },
        ],
      },
    ],
  },
  {
    dependenciesTitle: 'Submit Company Details & Formation Year',
    label:
      'Please make sure to confirm your full company name including entity type (eg. LLC) and the email address. Then,please verify the formation year of your business',
    type: 'information',
    dependencies: [
      {
        label: 'Company Name *',
        name: 'companyname',
        type: 'text',
      },
      {
        label: 'Company Email Address *',
        name: 'companyEmail',
        type: 'text',
      },
      {
        label: 'Foreign Pooled Investment Vehicle *',
        name: 'foreignpoolediv',
        type: 'checkbox',
      },
      {
        label: 'Request a FinCEN ID for this company *',
        name: 'getfincenid',
        type: 'checkbox',
      },
      {
        label: 'Formation Year *',
        name: 'formationyear',
        type: 'radio',
        options: FormationYearOptions, // Not yet
      },
    ],
  },
  {
    dependenciesTitle: 'Filing Type',
    label:
      'Select the filing type for your FinCEN Report, if this is your first time filing with FinCEN choose "Initial Report"',
    type: 'information',
    dependencies: [
      {
        label: 'Filing Type *',
        name: 'reporttype',
        type: 'select',
        options: FillingTypeOptions,
      },
    ],
  },
  {
    dependenciesTitle: 'Tax Information',
    label:
      'Choose EIN if your reporting company has a U.S. Employer Identification Number (EIN). Choose SSN-ITIN if your company uses a U.S. Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN) as its tax identification number. Choose “Foreign” if your company has a tax identification number issued by a foreign jurisdiction and does not have a U.S. tax identification number.',
    type: 'information',
    dependencies: [
      {
        label: 'Tax Identification Type *',
        name: 'taxtype',
        type: 'radio',
        options: TaxIdentificationOptions,
        dependencies: [
          {
            predicate: (v) => v === 'EIN',
            name: 'taxnumber',
            type: 'maskedInput',
            label: 'Tax Identification Number (EIN)*',
            mask: '99-9999999',
          },
          {
            predicate: (v) => v !== 'EIN',
            name: 'taxnumber',
            type: 'maskedInput',
            label: 'Tax Identification Number *',
            mask: '999999999',
          },
          {
            predicate: (v) => v === 'Foreign',
            name: 'taxcountry',
            type: 'country',
            label: 'Country/Jurisdiction (If foreign tax ID only) *',
          },
        ],
      },
      {
        label: 'Country/Jurisdiction of Formation *',
        type: 'country',
        name: 'countryjurisdiction',
      },
      {
        label: 'State of Formation (If country/jurisdiction is USA) *',
        type: 'state',
        name: 'formationstate',
      },
      {
        label: 'Tribal Jurisdiction of Formation (Optional)',
        type: 'text',
        name: 'tribaljurisdiction',
      },
    ],
  },
  // Address and other fields!
  {
    dependenciesTitle: 'Enter your U.S Principal Address of Business',
    type: 'information',
    dependencies: [
      {
        type: 'text',
        label: 'Address *',
        name: 'mailingaddress',
      },
      {
        type: 'state',
        label: 'State *',
        name: 'state',
      },
      {
        type: 'text',
        label: 'City *',
        name: 'mailingcity',
      },
      {
        type: 'text',
        label: 'Zip Code *',
        name: 'zip',
      },
    ],
  },
  // Remaining fields
  {
    dependenciesTitle: 'Submit FinCEN Report Information',
    type: 'information',
    dependencies: [
      {
        name: 'notes',
        type: 'textarea',
        label: 'Provide any additional information',
      },
      {
        name: 'reachPhoneNumber',
        type: 'text',
        label:
          'At what number can your accountant reach you if there are questions?',
      },
      {
        name: 'whatIsTheBestTimeToReachYouAtThisNumber',
        type: 'radio',
        label: 'What is the best time to reach you at this number?',
        options: ['Anytime', 'Morning', 'Afternoon', 'Evening'].map((o) => ({
          label: o,
          value: o,
        })),
      },
      {
        type: 'information',
        label:
          'To the best of my knowledge, the information provided in this FinCEN report is accurate and includes all necessary details about the company ownership, formation, and required submitter information. I understand that submitting incorrect or incomplete information may result in compliance issues or delays.',
        dependencies: [
          {
            name: 'agreement',
            type: 'checkbox',
            label: 'I agree',
          },
        ],
      },
    ],
  },
] as IBaseField<keyof IFincenReportForm>[];

// FormFields component
export function FormFields({
  updateValue,
  fincenReport,
  type,
}: {
  type: 'business' | 'individual';
  updateValue: (key: keyof IFincenReportForm, value: any) => void;
  fincenReport?: Partial<IFincenReport>;
}) {
  // Set the tax year to the next year automatically on load
  useEffect(() => {
    updateValue('year', nextYear); // Automatically set year to next year
  }, [updateValue]);

  return (
    <>
      {FincenReportFields.map((input, index) => (
        <Fragment key={index}>{renderInput(input)}</Fragment>
      ))}
    </>
  );
}
